/* eslint-disable react/jsx-props-no-spreading */
import PropTypes from 'prop-types';
import React, { useContext, useCallback, useState, useMemo } from 'react';

import * as AppPropTypes from '../lib/PropTypes';

const ListsContext = React.createContext(null);

export const useListsContext = () => useContext(ListsContext);

export const useList = (key) => {
    const {
        lists,
        setList: setAllLists,
        setListPaginated: setAllListsPaginated,
        clearList: clearAllLists,
    } = useListsContext();

    const list = useMemo(
        () => (lists !== null && key !== null ? lists[key] || null : null),
        [lists, key],
    );

    const listItems = useMemo(
        () =>
            list !== null
                ? list.reduce((its, pages) => {
                      const { data = null, pagination = null } = pages || [];
                      if (data == null && pagination === null) {
                          // Its a plain list
                          return its.concat(pages);
                      }
                      if (data !== null) {
                          // Its a paginated one
                          return its.concat(data);
                      }
                      return its;
                  }, [])
                : null,
        [list],
    );

    const setList = useCallback(
        (newList) => {
            if (key !== null) {
                setAllLists(key, newList);
            }
        },
        [key, setAllLists],
    );

    const setListPaginated = useCallback(
        (newList) => {
            if (key !== null) {
                setAllListsPaginated(key, newList);
            }
        },
        [key, setAllListsPaginated],
    );

    const clearList = useCallback(() => {
        if (key !== null) {
            clearList(key);
        }
    }, [key, clearAllLists]);

    return { list, listItems, setList, setListPaginated, clearList };
};

export const useLists = (key = null) => {
    const { lists, setLists, setList, setListPaginated, clearList, clearLists } = useListsContext();
    const list = useList(key);
    return {
        list,
        lists,
        setLists,
        setList,
        setListPaginated,
        clearList,
        clearLists,
    };
};

const propTypes = {
    // TODO: test this next prop
    // eslint-disable-next-line react/forbid-prop-types
    lists: PropTypes.any, // PropTypes.objectOf(AppPropTypes.pagination),
    children: PropTypes.node.isRequired,
};

const defaultProps = {
    lists: {},
};

export const ListsProvider = ({ lists: initialLists, children }) => {
    const [lists, setLists] = useState(initialLists);

    const setList = useCallback(
        (key, list = null) => {
            setLists({ ...lists, [key]: list });
        },
        [lists, setLists],
    );

    const setListPaginated = useCallback(
        (key, list = null) => {
            const { data = null, pagination = null } = list || {};
            setLists(
                Object.keys(lists).reduce((newPages, newKey) => {
                    if (key === newKey && data !== null && pagination !== null) {
                        const currentList = lists[key] || null;
                        const { data: currentData = null, pagination: currentPagination = null } =
                            currentList || {};
                        if (currentData !== null && currentPagination !== null) {
                            return {
                                ...newPages,
                                [key]: { data: [...currentData, ...data], pagination },
                            };
                        }
                        return { ...newPages, [key]: { data, pagination } };
                    }
                    return key === newKey
                        ? { ...newPages, [key]: list }
                        : { ...newPages, [key]: lists[key] };
                }),
            );
        },
        [lists, setLists],
    );

    const clearLists = useCallback(() => {
        setLists({});
    }, [setLists]);

    const clearList = useCallback(
        (key) => {
            setLists(
                Object.keys(lists).reduce(
                    (newPages, newKey) =>
                        key !== newKey ? { ...newPages, [key]: lists[key] } : newPages,
                    {},
                ),
            );
        },
        [lists, setLists],
    );

    const value = useMemo(
        () => ({
            lists,
            setList,
            setListPaginated,
            setLists,
            clearList,
            clearLists,
        }),
        [lists, setLists, setList, setListPaginated, clearLists, clearList],
    );

    return <ListsContext.Provider value={value}>{children}</ListsContext.Provider>;
};

ListsProvider.propTypes = propTypes;
ListsProvider.defaultProps = defaultProps;

export default ListsContext;
